import loadable from '@loadable/component';
import { ComponentType, FC, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useSiteConfig, useSiteMeta } from '../../scripts/selectors/global';
import type * as AwcMidnightCup3Module from './upsells/awcMidnightCup3';
import type * as BreakTheMetaFeedbackSurveyModule from './upsells/breakTheMetaFeedbackSurvey';
import type * as BreakTheMetaMidnightSeason1Module from './upsells/breakTheMetaMidnightSeason1';
import type * as ElekkGiveawayModule from './upsells/elekkGiveaway';
import type * as MdiMidnightGroupsModule from './upsells/mdiMidnightGroups';
import type * as MdiMidnightSeasonFinalsModule from './upsells/mdiMidnightSeasonFinals';
import type * as MdiReplayRaceMnS1Module from './upsells/mdiReplayRaceMnS1';
import type * as MidnightAddonsModule from './upsells/midnightAddons';
import type * as MidnightAffixesModule from './upsells/midnightAffixes';
import type * as MnSeason1RecapModule from './upsells/mnSeason1Recap';
import type * as MostOfRioMidnightModule from './upsells/mostOfRioMidnight';
import type * as RwfVenomousAbyssCoverageModule from './upsells/rwfVenomousAbyssCoverage';
import type * as TalentBuildsCrossPromoModule from './upsells/talentBuildsCrossPromo';
import { getNextUpsellScheduleBoundary, resolveUpsellQualifications, selectUpsellId } from './upsellSchedule';
import type { TUpsellId } from './upsellSchedule';
import type { TGameSite, TUpsellProps } from './types';

const MAX_TIMER_DELAY_MS = 2_147_483_647;

type TUpsellComponentProps = Omit<TUpsellProps<any>, 'fallback'>;

const AwcMidnightCup3Upsell = loadable(() => import('./upsells/awcMidnightCup3'), {
  resolveComponent: (module: typeof AwcMidnightCup3Module) => module.AwcMidnightCup3Upsell,
  fallback: null
});

const BreakTheMetaFeedbackSurveyUpsell = loadable(() => import('./upsells/breakTheMetaFeedbackSurvey'), {
  resolveComponent: (module: typeof BreakTheMetaFeedbackSurveyModule) => module.BreakTheMetaFeedbackSurveyUpsell,
  fallback: null
});

const BreakTheMetaMidnightSeason1Upsell = loadable(() => import('./upsells/breakTheMetaMidnightSeason1'), {
  resolveComponent: (module: typeof BreakTheMetaMidnightSeason1Module) => module.BreakTheMetaMidnightSeason1Upsell,
  fallback: null
});

const ElekkGiveawayUpsell = loadable(() => import('./upsells/elekkGiveaway'), {
  resolveComponent: (module: typeof ElekkGiveawayModule) => module.ElekkGiveawayUpsell,
  fallback: null
});

const MdiMidnightGroupsUpsell = loadable(() => import('./upsells/mdiMidnightGroups'), {
  resolveComponent: (module: typeof MdiMidnightGroupsModule) => module.MdiMidnightGroupsUpsell,
  fallback: null
});

const MdiMidnightSeasonFinalsUpsell = loadable(() => import('./upsells/mdiMidnightSeasonFinals'), {
  resolveComponent: (module: typeof MdiMidnightSeasonFinalsModule) => module.MdiMidnightSeasonFinalsUpsell,
  fallback: null
});

const MdiReplayRaceMnS1Upsell = loadable(() => import('./upsells/mdiReplayRaceMnS1'), {
  resolveComponent: (module: typeof MdiReplayRaceMnS1Module) => module.MdiReplayRaceMnS1Upsell,
  fallback: null
});

const MidnightAddonsUpsell = loadable(() => import('./upsells/midnightAddons'), {
  resolveComponent: (module: typeof MidnightAddonsModule) => module.MidnightAddonsUpsell,
  fallback: null
});

const MidnightAffixesUpsell = loadable(() => import('./upsells/midnightAffixes'), {
  resolveComponent: (module: typeof MidnightAffixesModule) => module.MidnightAffixesUpsell,
  fallback: null
});

const MnSeason1RecapUpsell = loadable(() => import('./upsells/mnSeason1Recap'), {
  resolveComponent: (module: typeof MnSeason1RecapModule) => module.MnSeason1RecapUpsell,
  fallback: null
});

const MostOfRioMidnightUpsell = loadable(() => import('./upsells/mostOfRioMidnight'), {
  resolveComponent: (module: typeof MostOfRioMidnightModule) => module.MostOfRioMidnightUpsell,
  fallback: null
});

const RwfVenomousAbyssCoverageUpsell = loadable(() => import('./upsells/rwfVenomousAbyssCoverage'), {
  resolveComponent: (module: typeof RwfVenomousAbyssCoverageModule) => module.RwfVenomousAbyssCoverageUpsell,
  fallback: null
});

const TalentBuildsCrossPromoUpsell = loadable(() => import('./upsells/talentBuildsCrossPromo'), {
  resolveComponent: (module: typeof TalentBuildsCrossPromoModule) => module.TalentBuildsCrossPromoUpsell,
  fallback: null
});

const LOADABLE_UPSELLS: Record<TUpsellId, ComponentType<TUpsellComponentProps>> = {
  'awc-midnight-cup-3': AwcMidnightCup3Upsell,
  'break-the-meta-feedback-survey': BreakTheMetaFeedbackSurveyUpsell,
  'break-the-meta-midnight-season-1': BreakTheMetaMidnightSeason1Upsell,
  'elekk-giveaway': ElekkGiveawayUpsell,
  'mdi-midnight-groups': MdiMidnightGroupsUpsell,
  'mdi-midnight-season-finals': MdiMidnightSeasonFinalsUpsell,
  'mdi-replay-race-midnight-season-1': MdiReplayRaceMnS1Upsell,
  'midnight-addons': MidnightAddonsUpsell,
  'midnight-affixes': MidnightAffixesUpsell,
  'midnight-season-1-recap': MnSeason1RecapUpsell,
  'most-of-rio-midnight': MostOfRioMidnightUpsell,
  'rwf-venomous-abyss-coverage': RwfVenomousAbyssCoverageUpsell,
  'talent-builds-cross-promo': TalentBuildsCrossPromoUpsell
};

export const Upsell: FC<TUpsellProps> = (props: TUpsellProps) => {
  const siteConfig = useSiteConfig();
  const siteMeta = useSiteMeta();
  const location = useLocation();
  const [selectionTime, setSelectionTime] = useState(Date.now);

  const game = RIO_SITE.game as TGameSite;
  const source = props.source || 'top';

  const qualifications = useMemo(
    () =>
      resolveUpsellQualifications({
        game,
        pathname: location.pathname,
        source,
        siteConfig,
        siteMeta
      }),
    [game, location.pathname, siteConfig, siteMeta, source]
  );

  useEffect(() => {
    const nextBoundary = getNextUpsellScheduleBoundary(game, new Date(selectionTime));
    if (!nextBoundary) {
      return;
    }

    const timeout = setTimeout(
      () => setSelectionTime(Date.now()),
      Math.min(nextBoundary.getTime() - selectionTime, MAX_TIMER_DELAY_MS)
    );

    return () => clearTimeout(timeout);
  }, [game, selectionTime]);

  const selectedUpsellId = useMemo(
    () =>
      selectUpsellId({
        game,
        now: new Date(selectionTime),
        qualifications,
        seed: siteConfig.seed,
        sequenceIndex: props.seedOffset
      }),
    [game, props.seedOffset, qualifications, selectionTime, siteConfig.seed]
  );

  if (!selectedUpsellId) {
    return props.fallback ?? null;
  }

  const SelectedComponent = LOADABLE_UPSELLS[selectedUpsellId];

  // `fallback` is also a reserved @loadable/component prop. Do not forward the
  // weighted-selection fallback and briefly render it while this chunk loads.
  return (
    <SelectedComponent
      className={props.className}
      qualification={qualifications.get(selectedUpsellId)}
      seedOffset={props.seedOffset}
    />
  );
};
